// The CRM ops list (MS-6630, T6.5): outbox rows needing attention across bookings, read from
// GET /bookings/crm/events/ (MS-6615) and filtered by status, provider, machine reason, project and
// creation date. The filters live in the page's URL so a list can be bookmarked and sent to a
// colleague; this module turns that URL into the server's query and back, and decides nothing about
// rendering, so every rule here is tested without a DOM.
//
// The rows are the redacted view (MS-6646): reason, instruction, codes and timestamps, plus the
// masked subject and the booking's code / unit / project. Never the request, the response or the
// provider's verbatim message.

import moment from 'moment-timezone';
import qs from 'qs';

import isbase64 from './isbase64';
import { tenantCRM, tenantProviderLabel } from './crmConfig';

export const LIST_PATH = '/integrations/crm/';
export const LIST_LIMIT = 50;
export const DEFAULT_STATUS = 'needs_human,failed';

// Status choices the list offers. The default is the server's default: everything parked.
export const STATUS_OPTIONS = [
  { value: 'needs_human,failed', text: 'Needs attention or failed' },
  { value: 'needs_human', text: 'Needs attention' },
  { value: 'failed', text: 'Failed' },
  { value: 'resolved', text: 'Resolved' },
  { value: 'sent', text: 'Sent' },
  { value: 'pending,in_flight', text: 'Pending or sending' },
  { value: 'superseded', text: 'Superseded' },
];

// Every machine reason the worker parks a row with, in the runbook's order
// (book-api docs/guides/resolve-a-crm-sync-failure.md), with a short label. The machine reason is
// always shown beside the label: it is what the runbook and the alert are keyed by.
export const REASONS = [
  { value: 'duplicate_booking_id', text: 'Duplicate booking id at the CRM' },
  { value: 'contact_not_found', text: 'Contact not found at the CRM' },
  { value: 'booking_not_found', text: 'Booking not found at the CRM' },
  { value: 'validation', text: 'CRM rejected a field' },
  { value: 'company_unmapped', text: 'Company not mapped' },
  { value: 'mapping_inconsistent', text: 'Mapping inconsistent' },
  { value: 'auth', text: 'CRM refused our credentials' },
  { value: 'timeout_non_idempotent', text: 'Timed out; outcome unknown' },
  { value: 'probe_contact_missing', text: 'Probe: contact missing' },
  { value: 'probe_inconclusive', text: 'Probe inconclusive' },
  { value: 'unexpected_response', text: 'Unexpected CRM response' },
  { value: 'builder_pending', text: 'Call not built yet' },
  { value: 'reinstate_unsupported', text: 'Reinstate not supported by the CRM' },
  { value: 'contact_ref_missing', text: 'Contact reference missing' },
  { value: 'booking_ref_missing', text: 'Booking reference missing' },
  { value: 'state_mismatch', text: 'CRM state differs from ours' },
  { value: 'status_not_sendable', text: 'Status the CRM cannot take' },
  { value: 'previous_booking_lost', text: 'Previous booking lost at the CRM' },
  { value: 'plan_failed', text: 'Could not plan the call' },
  { value: 'lease_expired_non_idempotent', text: 'Lease expired mid-call; outcome unknown' },
  { value: 'ambiguous_result_non_idempotent', text: 'Ambiguous result; outcome unknown' },
  { value: 'panic_after_send_non_idempotent', text: 'Crashed after sending; outcome unknown' },
  { value: 'cancel_ambiguous', text: 'Cancel while the booking\'s existence is uncertain' },
  { value: 'retry_budget_exhausted', text: 'Retries exhausted' },
  { value: 'refs_write_failed', text: 'Sent, but the CRM ids could not be stored' },
  { value: 'provider_not_registered', text: 'Provider not registered' },
];

export function reasonLabel(reason) {
  const key = String(reason || '').trim();
  const found = REASONS.find(r => r.value === key);
  return found ? found.text : key;
}

// A reason or an xid as the server accepts it (handlers/crm_events.go crmFilterToken). Anything
// else is dropped here rather than sent to be refused.
const TOKEN = /^[A-Za-z0-9_-]{1,64}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const PAGE = /^\d{1,5}$/;
const MAX_PAGE = 10000; // the server's crmListMaxPage

function token(v) {
  const s = String(v || '').trim();
  return TOKEN.test(s) ? s : '';
}

// A calendar day the server will accept: the shape AND a real date (2026-02-31 is neither a
// filter nor an error worth a 400; it is dropped like any other value the server would refuse).
function day(v) {
  const s = String(v || '').trim();
  return DAY.test(s) && moment(s, 'YYYY-MM-DD', true).isValid() ? s : '';
}

// The status filter as the URL carries it, or the default. Unknown values fall back to the default
// rather than being sent: the server would answer 400 and the page would have nothing to show.
export function statusOf(query) {
  const s = String((query && query.status) || '').trim();
  return STATUS_OPTIONS.some(o => o.value === s) ? s : DEFAULT_STATUS;
}

// The whole value must be a number: parseInt would turn a bookmarked `page=3x` into 3.
export function pageOf(query) {
  const s = String((query && query.page) || '').trim();
  if (!PAGE.test(s)) return 1;
  const n = parseInt(s, 10);
  return n >= 1 && n <= MAX_PAGE ? n : 1;
}

// projectFilterOf reads the {id, name} the project search stored (base64 of a query string, the
// same shape Integrations/Salesforce.jsx keeps), or null.
export function projectFilterOf(query) {
  const raw = query && query.projectFilter;
  if (typeof raw !== 'string' || !raw || !isbase64(raw)) return null;
  let parsed;
  try {
    parsed = qs.parse(atob(raw));
  } catch (e) {
    return null;
  }
  const id = token(parsed.id);
  if (!id) return null;
  return { id, name: String(parsed.name || '') };
}

export function projectFilterValue(project) {
  return btoa(qs.stringify({ id: project.id, name: project.name }));
}

// listParamsFrom turns the page's URL query into the server's.
export function listParamsFrom(query) {
  const q = query || {};
  const params = { status: statusOf(q), page: pageOf(q), limit: LIST_LIMIT };
  const provider = token(q.provider);
  if (provider) params.provider = provider;
  const reason = token(q.reason);
  if (reason) params.reason = reason;
  const project = projectFilterOf(q);
  if (project) params.project = project.id;
  let from = day(q.dateFrom);
  let to = day(q.dateTo);
  // A range picked in the wrong order is a range, not an error: the server refuses from > to, so
  // the two are swapped rather than sent to be refused or silently dropped.
  if (from && to && from > to) [from, to] = [to, from];
  if (from) params.from = from;
  if (to) params.to = to;
  return params;
}

// canSeeCrmOps is who the ops list is for: the route's ACL (MS-6615: super admin, admin, admin
// basic), used for the actions, the dashboard tile and its badge alike so no role is shown a tile
// that leads to a refusal, and none that may act is hidden from it.
export function canSeeCrmOps(auth) {
  const a = auth || {};
  return Boolean(a.isSuperAdmin || a.isAdmin || a.isAdminBasic);
}

// bookingEventsPath is the booking's own events route. gorilla/csrf scopes its cookie to the
// path that issued it, so a token from the cross-booking list would not travel with a POST to
// this path; the token for an action is read from here first.
export function bookingEventsPath(row) {
  return `/bookings/${encodeURIComponent(row.bookingXID)}/crm/events/`;
}

export function listPath(params) {
  return `/bookings/crm/events/?${qs.stringify(params)}`;
}

// queryWith is the next URL query after a filter change: the change applied, empty values removed,
// and the page reset to the first (a filter that moves the list must not leave page 7 of nothing).
export function queryWith(query, changes, { keepPage = false } = {}) {
  const next = Object.assign({}, query || {}, changes || {});
  Object.keys(next).forEach((k) => {
    if (next[k] === '' || next[k] === null || next[k] === undefined) delete next[k];
  });
  if (!keepPage) delete next.page;
  return next;
}

// The CRMs the tenant works with, as filter choices; the label is the tenant's own name for each.
export function providerOptions(appConfig) {
  return tenantCRM(appConfig).providers
    .map(p => ({ value: p.provider, text: tenantProviderLabel(appConfig, p.provider) }));
}

export function bookingPath(row) {
  const xid = row && row.bookingXID;
  return xid ? `/bookings/${encodeURIComponent(xid)}/` : '';
}

// bookingLabel is how a row names its booking: the code when the server found the booking, the
// xid otherwise (a booking that is gone still had rows).
export function bookingLabel(row) {
  const b = (row && row.booking) || {};
  return b.code || (row && row.bookingXID) || '';
}

export function unitAndProject(row) {
  const b = (row && row.booking) || {};
  return [b.unitReference, b.projectShortName].filter(Boolean).join(', ');
}

export function actionPath(row, action) {
  return `/bookings/${encodeURIComponent(row.bookingXID)}/crm/events/${encodeURIComponent(row.id)}/${action}/`;
}



// WEBPACK FOOTER //
// ./src/utils/crmOps.js